import { apiService } from './ApiService';
import { AxiosResponse } from 'axios';

/** One visibility rule: `field` names a list inside a cart item (segment ending in []) and a
 *  row field, e.g. "integration_data.dms_data[].location". include = keep only matching rows, exclude = drop them,
 *  prioritize = keep every row but list matching ones first, in `values` order. */
export type VisibilityRule = { field: string; mode: 'include' | 'exclude' | 'prioritize'; values: string[] };

class OrganizationService {
    private static instance: OrganizationService;

    public static getInstance(): OrganizationService {
        if (!OrganizationService.instance) {
            OrganizationService.instance = new OrganizationService();
        }
        return OrganizationService.instance;
    }

    public async getOrganizationInfo(): Promise<AxiosResponse<any>> {
        return apiService.get('/api/organization/info');
    }

    public async createOrganization(data: { name: string; description?: string }): Promise<AxiosResponse<any>> {
        return apiService.post('/api/organization/create', data);
    }

    public async getOrganizationSettings(): Promise<AxiosResponse<any>> {
        return apiService.get('/api/organization/settings');
    }

    public async updateOrganizationSettings(settings: any): Promise<AxiosResponse<any>> {
        return apiService.post('/api/organization/settings', settings);
    }

    public async getOrganizationMembers(): Promise<AxiosResponse<any>> {
        return apiService.get('/api/organization/members');
    }

    public async addOrganizationMember(data: { user_email: string; role: string }): Promise<AxiosResponse<any>> {
        return apiService.post('/api/organization/members', data);
    }

    public async removeOrganizationMember(userUid: string): Promise<AxiosResponse<any>> {
        return apiService.delete(`/api/organization/members/${userUid}`);
    }

    /** Empty list = no limits. `homeCity` is the member's home city (matches the org
     *  warehouses' city/name): every warehouse in it counts as home, and the server folds
     *  them into a "home first" priority rule. */
    public async setMemberPermissions(userUid: string, rules: VisibilityRule[], homeCity?: string | null): Promise<AxiosResponse<any>> {
        const body: any = { rules };
        // undefined/null = leave the home city unchanged; '' = clear it.
        if (homeCity !== undefined && homeCity !== null) body.home_city = homeCity;
        return apiService.put(`/api/organization/members/${userUid}/permissions`, body);
    }

    public async toggleUseOrganizationSettings(useOrgSettings: boolean): Promise<AxiosResponse<any>> {
        return apiService.post('/api/user/settings/toggle-org-settings', { use_org_settings: useOrgSettings });
    }

    // ── Organization logo ────────────────────────
    public async uploadOrganizationLogo(file: File): Promise<AxiosResponse<{ logo_url: string }>> {
        const formData = new FormData();
        formData.append('file', file);
        return apiService.post('/api/organization/logo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    public async deleteOrganizationLogo(): Promise<AxiosResponse<{ logo_url: null }>> {
        return apiService.delete('/api/organization/logo');
    }

    // ── Guest invite links ───────────────────────
    public async uploadGuestInviteIcon(file: File): Promise<AxiosResponse<{ url: string }>> {
        const formData = new FormData();
        formData.append('file', file);
        return apiService.post('/api/organization/guest-invite/upload-icon', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    public async createGuestInvite(data: {
        label?: string;
        max_uses?: number;
        expires_in_hours?: number;
        visitor_name?: string;
        visitor_email?: string;
        visitor_phone?: string;
        icon_url?: string;
        header_text?: string;
        allowed_domain?: string;
    }): Promise<AxiosResponse<any>> {
        return apiService.post('/api/organization/guest-invite', data);
    }

    public async getGuestInvites(): Promise<AxiosResponse<{ invites: any[] }>> {
        return apiService.get('/api/organization/guest-invite');
    }

    public async revokeGuestInvite(token: string): Promise<AxiosResponse<any>> {
        return apiService.delete(`/api/organization/guest-invite/${token}`);
    }

    public async getGuestLeads(): Promise<AxiosResponse<{ leads: GuestLead[] }>> {
        return apiService.get('/api/organization/guest-leads');
    }

    public async getGuestLeadConversation(adkSessionId: string): Promise<AxiosResponse<GuestConversation>> {
        return apiService.get(`/api/organization/guest-leads/${adkSessionId}/conversation`);
    }

    public async convertLeadToOrder(adkSessionId: string): Promise<AxiosResponse<{
        order_id: number;
        external_order_id: string;
        session_id: string;
        status: string;
        already_existed: boolean;
        name: string | null;
        vin: string | null;
        buyer: string | null;
    }>> {
        return apiService.post(`/api/organization/guest-leads/${adkSessionId}/convert`, {});
    }
}

export interface SelectedPart {
    // Part identification
    partId?: string;
    part_callout_id?: string | null;
    // Structured catalog data (nested)
    structured_data?: {
        part_data?: {
            part_id?: string;
            part_number?: string;
            description?: string;
            price?: string;
        };
        [key: string]: any;
    };
    // Flat fields sometimes stored directly
    partNumber?: string;
    description?: string;
    category?: string;
    pricing?: any;
    probability_score?: number;
    related_user_query?: string;
    complementary_parts?: Array<{ part_number?: string; description?: string }>;
    [key: string]: any;
}

export interface GuestLead {
    id: string;
    invite_id: number;
    invite_label: string;
    invite_token: string;
    is_invite_active: boolean;
    // null when no one has visited yet (placeholder row)
    guest_uid: string | null;
    adk_session_id: string | null;
    language: string | null;
    first_message: string | null;
    message_count: number;
    has_session: boolean;
    // Visitor identity (from invite pre-fill or URL query params at visit time)
    visitor_name: string | null;
    visitor_email: string | null;
    visitor_phone: string | null;
    expires_at: string | null;
    created_at: string;
    updated_at: string;
    // Parts identified/selected during the session
    selected_parts: SelectedPart[];
    parts_count: number;
}

export interface ConversationMessage {
    role: 'user' | 'assistant';
    content: string;
    timestamp?: string;
    parts?: Array<{ type: string; content: string; url?: string; mimeType?: string }>;
}

export interface GuestConversation {
    status: string;
    session: {
        session_id: string;
        user_id: string;
        app_name: string;
        created_at: string;
        updated_at: string;
        state: Record<string, unknown>;
        conversation_history: ConversationMessage[];
        event_count: number;
    };
}

export const organizationService = OrganizationService.getInstance();
